import "./EditCoPlayersPopup.scss";
import MemberIdTag from "../../../components/UI/MemberIdTag/MemberIdTag";
import { useCallback, useEffect, useRef, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import {
  IBooking,
  ICoach,
  ICourt,
  ICourtType,
  IGuest,
  IIndividualWithStatus,
  IPermission,
  IPrices,
  ISummaryItem,
  IUser,
} from "../../../types/api";
import { toast } from "react-toastify";
import {
  freeOnJuniorDropIn,
  isJuniorType,
} from "../../../shared/memberType";
import { CognitoUser } from "../../../types/cognito";
import { capitalizeWords, formatDateToSwedish } from "../../../shared/utility";
import time from "../../../assets/images/time.svg";
import greenCheck from "../../../assets/images/green-check.svg";
import useBeforeUnload from "../../../shared/hooks/useBeforeUnload";
import singleCross from "../../../assets/images/single-corss.svg";
import { searchUsers } from "../../../api/users/users";
import { BookingEnum, StatusEnum } from "../../../shared/enums";
import { confirmBooking, updateBooking } from "../../../api/bookings/bookings";
import { getSlotByMember } from "../../../api/summaries/summaries";
import { getGuestByMemberId } from "../../../api/guests/guests";
import e from "express";

interface IEditCoPlayersPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: (bookingConfirmResponse: IBooking) => void;
  cognitoUser: CognitoUser | null;
  user: IUser | null;
  booking?: IBooking;
  coachesList: string[] | [];
  latestCoPlayers: IUser[] | [];
  coaches: ICoach[];
  courts: ICourt[];
  userData?: IUser;
  prices: IPrices[];
  permissions: IPermission[];
  isAdmin: boolean;
  guestDataFromAdmin?: IGuest[];
}
interface IAdditiveOption {
  value: "TRAINER" | "CO-PLAYER" | "HITTING_PARTNER";
}
function EditCoPlayersPopup(props: IEditCoPlayersPopupProps) {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);

  useBeforeUnload(
    "Din bokning kommer att avbrytas om du lämnar sidan.",
    props.showPopup,
    () => handleClose,
  );

  const { coachesList, latestCoPlayers, coaches, booking } = props;
  const [chosenCoPlayers, setChosenCoPlayers] = useState<IUser[]>([]);
  const [previousChosenCoPlayers, setPreviousChosenCoPlayers] = useState<
    IUser[]
  >([]);
  const [chosenCoach, setChosenCoach] = useState<string>("");
  const [availableCoPlayers, setAvailableCoPlayers] = useState<IUser[]>([]);
  const [guests, setGuests] = useState<string[]>([]);
  const [guestsData, setGuestsData] = useState<IGuest[]>([]);
  const [totalCoPlayers, setTotalCoPlayers] = useState<number>(0);
  const [currentAdditiveChoice, setCurrentAdditiveChoice] =
    useState<IAdditiveOption>({ value: "CO-PLAYER" });
  const [currentAdditiveText, setCurrentAdditiveText] =
    useState<string>("Välj medspelare");
  const [isDropIn, setIsDropIn] = useState<boolean>(false);

  const memberType =
    props.cognitoUser?.signInUserSession.idToken.payload["cognito:groups"];

  const [coPlayerSlots, setCoPlayerSlots] = useState<{
    [key: string]: ISummaryItem;
  }>({});
  const [guestSlot, setGuestSlot] = useState<ISummaryItem | null>(null);
  const [sendEmail, setSendEmail] = useState<boolean>(true);

  const additiveOptions = [
    { label: "Medspelare", value: "CO-PLAYER", text: "Välj medspelare" },
    { label: "Tränare", value: "TRAINER", text: "Välj tränare" },
    {
      label: "Hitting Partner",
      value: "HITTING_PARTNER",
      text: "Välj Hitting Partner",
    },
  ];

  const fetchUsersFromBooking = async () => {
    if (!booking) return;
    let guests: string[] = [];

    booking.guests.forEach((guest) => (guests = [...guests, guest.member_id]));
    setGuests(guests);
    props.guestDataFromAdmin &&
      setGuestsData(
        props.guestDataFromAdmin.filter((g) => guests.includes(g.member_id)),
      );
    if (booking.co_players.length === 0) return;
    const usersToFetch = booking.co_players.map((player) => player.member_id);
    if (usersToFetch) {
      try {
        const response = await Promise.all(
          usersToFetch.map((user) => searchUsers(user, "user", true, "CO_PLAYER")),
        );
        let users: IUser[] = [];
        response.forEach((user) => {
          users = [...users, ...user];
        });
        setChosenCoPlayers(users);
      } catch (error: any) {
        toast.error(error);
      }
    }
  };
  const handleCoPlayerSlot = async (
    datetime_start: string,
    member_id: string,
    court_id: string,
    court_type: ICourtType,
    co_player_type?: string,
  ) => {
    const isDropIn = booking?.booking_type === BookingEnum.DROP_IN;
    try {
      // A junior's drop-in is priced by its own rules (see freeOnJuniorDropIn),
      // so it asks for the ordinary price and applies the free cases here.
      const juniorDropIn = isDropIn && isJuniorType(props.user?.member_type);
      const response = await getSlotByMember(
        datetime_start,
        court_id,
        court_type,
        member_id,
        isDropIn && !juniorDropIn,
      );
      if (
        juniorDropIn &&
        response &&
        !("message" in response) &&
        freeOnJuniorDropIn(co_player_type, datetime_start)
      ) {
        return { ...response, price: 0 };
      }
      return response;
    } catch (error: any) {
      toast.error(error);
    }
  };
  const handleGuestSlot = async (
    datetime_start: string,
    court_id: string,
    court_type: ICourtType,
  ) => {
    try {
      const response = await getSlotByMember(
        datetime_start,
        court_id,
        court_type,
      );
      return response;
    } catch (error: any) {
      toast.error(error);
    }
  };
  useEffect(() => {
    if (
      chosenCoPlayers.length > 0 &&
      previousChosenCoPlayers !== chosenCoPlayers
    ) {
      const courtType = props.courts.find(
        (c) => c.court_id === props.booking?.court_id,
      )?.court_type;
      const promises = chosenCoPlayers.map((player) =>
        handleCoPlayerSlot(
          props.booking?.datetime_start || "",
          player.member_id,
          props.booking?.court_id || "",
          courtType as ICourtType,
          player.member_type,
        ).then((response) => ({ [player.member_id]: response })),
      );
      Promise.all(promises).then((responses) => {
        const slots = responses.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {},
        );
        setCoPlayerSlots((prev) => ({ ...prev, ...slots }));
      });
      setPreviousChosenCoPlayers(chosenCoPlayers);
    }
  }, [chosenCoPlayers]);

  useEffect(() => {
    let notSelectedGuests = guests.filter((guest) => guest === "guest");

    if (
      (notSelectedGuests.length > 0 && notSelectedGuests.length <= 1) ||
      (guestSlot === null && notSelectedGuests.length > 0)
    ) {
      const courtType = props.courts.find(
        (c) => c.court_id === props.booking?.court_id,
      )?.court_type;
      handleGuestSlot(
        props.booking?.datetime_start || "",
        props.booking?.court_id || "",
        courtType as ICourtType,
      ).then((response) => {
        setGuestSlot(response);
      });
    }
  }, [guests]);

  useEffect(() => {
    if (props.showPopup) {
      fetchUsersFromBooking();
      setAvailableCoPlayers(latestCoPlayers);
      if (props.booking && props.booking.coach_id.length > 0) {
        setChosenCoach(props.booking.coach_id[0]);
      }
      if (props.booking) {
        setIsDropIn(props.booking.booking_type === BookingEnum.DROP_IN);
      }
    }
  }, [props.showPopup, booking, latestCoPlayers]);

  useEffect(() => {
    setTotalCoPlayers(chosenCoPlayers.length + guests.length);
  }, [guests, chosenCoPlayers]);

  useEffect(() => {
    setAvailableCoPlayers(latestCoPlayers);
  }, [latestCoPlayers, props.showPopup]);

  const handleClose = () => {
    setSearchString("");
    setSearchedUsers([]);
    setChosenCoPlayers([]);
    setChosenCoach("");
    setCurrentAdditiveChoice({ value: "CO-PLAYER" });
    setGuests([]);
    props.onClose();
  };

  const handleConfirm = (bookingConfirmResponse: IBooking) => {
    setSearchString("");
    setSearchedUsers([]);
    setChosenCoPlayers([]);
    setCurrentAdditiveChoice({ value: "CO-PLAYER" });
    setGuests([]);
    props.onConfirm(bookingConfirmResponse);
    props.onClose();
  };
  const handleAdditiveChoiceToggle = (value: IAdditiveOption) => {
    setCurrentAdditiveChoice(value);
    const selectedOption = additiveOptions.find(
      (option) => option.value === value.value,
    );
    if (selectedOption) {
      setCurrentAdditiveText(selectedOption.text);
    }
  };

  const handleUpdateBooking = async () => {
    if (props.booking === undefined || isUpdating) return;
    setIsUpdating(true);
    let removedCoPLayersList: string[] = [];
    let addedCoPlayersList: string[] = [];
    let removedCoachId: string | undefined =
      booking?.coach_id[0] !== chosenCoach ? booking?.coach_id[0] : "";
    let addedCoachId: string | undefined =
      booking?.coach_id[0] !== chosenCoach && chosenCoach !== ""
        ? chosenCoach
        : "";

    let addedGuestsList: string[] = [];
    let removedGuestsList: string[] = [];

    if (addedCoachId === undefined) {
      addedCoachId = "";
    }
    if (removedCoachId === undefined) {
      removedCoachId = "";
    }

    const removedCoPLayers = props.booking.co_players.filter(
      (player) =>
        !chosenCoPlayers.some(
          (chosenPlayer) => chosenPlayer.member_id === player.member_id,
        ),
    );
    removedCoPLayers.forEach((player) => {
      removedCoPLayersList.push(player.member_id);
    });

    const addedCoPlayers = chosenCoPlayers.filter(
      (player) =>
        !props.booking?.co_players.some(
          (chosenPlayer) => chosenPlayer.member_id === player.member_id,
        ),
    );
    addedCoPlayers.forEach((player) => {
      addedCoPlayersList.push(player.member_id);
    });

    const bookingGuestCount = props.booking.guests.filter(
      (guest) => guest.member_id === "guest",
    ).length;
    const currentGuestCount = guests.filter(
      (guest) => guest === "guest",
    ).length;

    if (currentGuestCount > bookingGuestCount) {
      const addedDefaultGuests = Array(
        currentGuestCount - bookingGuestCount,
      ).fill("guest");
      addedGuestsList.push(...addedDefaultGuests);
    } else if (currentGuestCount < bookingGuestCount) {
      const removedDefaultGuests = Array(
        bookingGuestCount - currentGuestCount,
      ).fill("guest");
      removedGuestsList.push(...removedDefaultGuests);
    }

    // Handle guests with unique member_ids (not "guest")
    const removedGuests = props.booking.guests.filter(
      (guest) =>
        guest.member_id !== "guest" && !guests.includes(guest.member_id),
    );
    removedGuests.forEach((guest) => {
      removedGuestsList.push(guest.member_id);
    });

    const addedGuests = guests.filter(
      (guest) =>
        guest !== "guest" &&
        !props.booking?.guests.some((g) => g.member_id === guest),
    );
    addedGuests.forEach((guest) => {
      addedGuestsList.push(guest);
    });
    let memberType = undefined;
    if (!props.isAdmin) {
      memberType = "user" as "admin" | "user";
    } else {
      memberType = "admin" as "admin" | "user";
    }
    let sendMail = true;
    if (props.isAdmin) {
      sendMail = sendEmail;
    }
    const toastLoeader = toast.loading("Bekräftar ändringar...");

    try {
      const bookingConfirmResponse = await updateBooking(
        props.booking,
        removedCoPLayersList,
        addedCoPlayersList,
        removedGuestsList,
        addedGuestsList,
        memberType,
        isDropIn,
        sendMail,
        removedCoachId,
        addedCoachId,
      );
      if (bookingConfirmResponse && !("message" in bookingConfirmResponse)) {
        toast.dismiss(toastLoeader);
        toast.success("Ändringar bekräftade!");
        handleConfirm(bookingConfirmResponse);
      }
    } catch (error: any) {
      console.log(error);
      toast.dismiss(toastLoeader);
      toast.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const addCoach = (coach: string) => {
    setChosenCoach(coach);
    const isHittingPartner = coaches.find(
      (c) => c.coach_id === coach,
    )?.is_hitting_partner;
    toast.success(isHittingPartner ? "Hitting Partner vald" : "Tränare vald");
  };
  const removeCoach = (coach: string) => () => {
    setChosenCoach("");
    toast.success("Tränare borttagen");
  };
  const addPlayerAsCoPlayer = (player: IUser, source: string) => {
    if (isForbiddenCoPlayer(player)) {
      toast.error("Den här spelaren kan inte väljas");
      return;
    }
    if (
      (player.member_type === "Vilande spelrätt" ||
        player.member_type === "Vilande spelrätt..") &&
      chosenCoach !== ""
    ) {
      toast.warning(
        "Vilande spelrätt har inte behörighet att boka med tränare",
      );
      return;
    }
    setSearchString("");
    setSearchedUsers([]);
    if (totalCoPlayers === 3) {
      toast.error("Du kan inte välja fler än 3 medspelare");
      return;
    }
    setChosenCoPlayers((prev) => [...prev, { ...player, source }]);

    toast.success(
      `Medspelare ${
        capitalizeWords(player.first_name) +
        " " +
        capitalizeWords(player.last_name)
      } tillagd`,
    );
  };

  const addGuest = () => {
    setGuests((prev) => [...prev, "guest"]);
  };

  const removeCoPlayer = (player: any) => () => {
    setChosenCoPlayers((prev) =>
      prev.filter((p) => p.member_id !== player.member_id),
    );
    // if (player.source === "available") {
    //   setAvailableCoPlayers((prev) => [...prev, player]);
    // } else if (player.source === "filtered") {
    //   setFilteredOtherUsers((prev) => [...prev, player]);
    // }
    toast.success(
      `Medspelare ${
        capitalizeWords(player.first_name) +
        " " +
        capitalizeWords(player.last_name)
      } borttagen`,
    );
  };

  const lastWarningTimeRef = useRef<number>(0);

  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestUserSearchRef = useRef<string>("");

  const fetchSearchUsers = async (search: string) => {
    latestUserSearchRef.current = search;
    const isAdmin = props.isAdmin;
    let requestor = "user" as "admin" | "user";
    if (isAdmin) {
      requestor = "admin" as "admin" | "user";
    }
    try {
      const response = await searchUsers(
        search,
        requestor,
        requestor === "admin",
        "CO_PLAYER",
      );
      if (latestUserSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }

      setSearchedUsers(response);
    } catch (error: any) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (searchString.length > 0) {
      handleSearchUsers();
    } else {
      setSearchedUsers([]);
    }
  }, [searchString]);

  const handleSearchUsers = () => {
    fetchSearchUsers(searchString);
  };

  const isForbiddenCoPlayer = (player: IUser) => {
   
    return (
      (memberType?.[0] === "user" &&
        (player.member_id === "9215" || player.member_id === "3783")) ||
      player.member_type === "Före detta medlem"
    );
  };

  /** End search users  */
  const startDate = new Date(booking?.datetime_start || "");
  const { weekday, dateAndMonth, hour } = formatDateToSwedish(
    new Date(startDate),
  );
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  const formattedEndHour = endDate.toTimeString().substring(0, 5);
  const dateString = `${weekday} ${dateAndMonth}`;
  const courtName = props.courts.find(
    (c) => c.court_id === booking?.court_id,
  )?.name;
  const courtType = props.courts.find(
    (c) => c.court_id === booking?.court_id,
  )?.court_type;

  const timeCourtString = `${hour} - ${formattedEndHour} ${courtName}`;

  const isReady = props.booking !== undefined;
  const chosenCoachData = chosenCoach
    ? coaches.find((c) => c.coach_id === chosenCoach)
    : null;

  // --- Hitting Partner tab logic ---
  // The slot's coach_available excludes the coach already booked on this slot,
  // so in the edit view include the booking's own coach as selectable — otherwise
  // removing it (unsaved) would leave no way to re-add it and the tab would vanish.
  const bookingCoachId = props.booking?.coach_id?.[0];
  const coachIds = coachesList as string[];
  const selectableCoachIds: string[] =
    bookingCoachId && bookingCoachId !== "" && !coachIds.includes(bookingCoachId)
      ? [...coachIds, bookingCoachId]
      : coachIds;

  const chosenCoachIsHittingPartner = !!chosenCoachData?.is_hitting_partner;
  const hasRegularCoachChosen =
    chosenCoach !== "" && !chosenCoachIsHittingPartner;
  const hittingPartnersAvailable = selectableCoachIds.some(
    (id) => coaches.find((c) => c.coach_id === id)?.is_hitting_partner,
  );
  // Exclusive: only offered when a hitting partner is available and no
  // co-player, regular coach or guest has been added.
  const canShowHittingPartnerTab =
    hittingPartnersAvailable &&
    chosenCoPlayers.length === 0 &&
    guests.length === 0 &&
    !hasRegularCoachChosen;

  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Din bokning{" "}
          {booking?.booking_type === BookingEnum.DROP_IN && "(Drop in)"}
        </span>
      }
      loading={!isReady}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form less-gap"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-top">
            {isReady && (
              <button
                className="text-m"
                onClick={() => handleUpdateBooking()}
                disabled={isUpdating}
              >
                Uppdatera bokning
              </button>
            )}
          </div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{dateString}</span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text ">Tid + Bana</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{timeCourtString}</span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>

              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Huvudbokare</span>
                <div className="text-m primary-text text-svg-wrapper">
                  {props.userData && (
                    <span>
                      {capitalizeWords(props.userData.first_name || "")}{" "}
                      {capitalizeWords(props.userData.last_name || "")}{" "}
                      <MemberIdTag
                        memberId={props.userData.member_id}
                        memberType={props.userData.member_type}
                      />
                    </span>
                  )}
                  {props.user && (
                    <span>
                      {capitalizeWords(props.user.first_name || "")}{" "}
                      {capitalizeWords(props.user.last_name || "")}{" "}
                      <MemberIdTag
                        memberId={props.user.member_id}
                        memberType={props.user.member_type}
                      />
                    </span>
                  )}
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between align-top">
                <span className="text-m primary-text">Medspelare</span>
                <span className="text-m primary-text text-svg-wrapper">
                  <div className="chosen-players">
                    {chosenCoPlayers.map((player, index) => {
                      const coPlayerPermission = props.permissions.find(
                        (permission) =>
                          permission.member_type === player.member_type,
                      );

                      let coPlayerPrice = 0;
                      const coPlayerSlot = coPlayerSlots[player.member_id];

                      const coPlayerPriceString =
                        coPlayerSlot !== undefined ? coPlayerSlot.price : 0;
                      coPlayerPrice = Number(coPlayerPriceString);
                      let isForetag = false;
                      if (
                        (coPlayerPermission &&
                          coPlayerPermission.member_type === "Företag") ||
                        (coPlayerPermission &&
                          coPlayerPermission.member_type === "Företag B")
                      ) {
                        isForetag = true;
                      }
                      if (booking) {
                        const start = new Date(booking.datetime_start);

                        // weekday: 1–5 are Mon–Fri
                        const weekday = start.getDay(); // 0=Sun, 6=Sat
                        const isWeekday = weekday >= 1 && weekday <= 5;

                        // extract time
                        const hour = start.getHours();

                        // 16:00–18:00 check
                        const isOutside16and18 = hour < 16 || hour > 18;
                        const hasEffectiveCoach = chosenCoach !== "";

                        // special rule check
                        const isSpecial5300Weekday =
                          booking.court_id === "5305" &&
                          hasEffectiveCoach &&
                          isWeekday &&
                          isOutside16and18;
                        const isSpecial5300Weekend =
                          booking.court_id === "5305" &&
                          hasEffectiveCoach &&
                          !isWeekday;
                        if (isSpecial5300Weekend) {
                          coPlayerPrice = 0;
                        }
                        if (isSpecial5300Weekday) {
                          coPlayerPrice = 0;
                        }
                      }
                      return (
                        <div key={index} className="chosen-player">
                          {capitalizeWords(player.first_name)}{" "}
                          {capitalizeWords(player.last_name)}{" "}
                          <MemberIdTag
                            memberId={player.member_id}
                            memberType={player.member_type}
                          />{" "}
                          {Number(coPlayerPrice)} kr
                          {isForetag && <div>(pris kan tillkomma)</div>}
                          <img
                            onClick={removeCoPlayer(player)}
                            src={singleCross}
                            alt="cross"
                            className="cross-img pointer"
                          ></img>
                        </div>
                      );
                    })}
                    {guests.map((guest, index) => {
                      let price = "";
                      price = guestSlot?.price || "";

                      let isCheckedInGuest;
                      if (guest !== "guest") {
                        isCheckedInGuest = guestsData.find(
                          (g) => g.member_id === guest,
                        );
                        price =
                          props.booking?.final_price
                            .find((e) => e.event.includes(guest))
                            ?.price.toString() || "";
                      }

                      return (
                        <div className="chosen-player" key={index}>
                          {guest !== "guest"
                            ? capitalizeWords(
                                isCheckedInGuest?.first_name +
                                  " " +
                                  isCheckedInGuest?.last_name,
                              ) +
                              " (" +
                              isCheckedInGuest?.member_id +
                              ")"
                            : "Gäst"}{" "}
                          ({price} kr)
                          <img
                            onClick={() =>
                              setGuests((prev) =>
                                prev.filter((g, i) => i !== index),
                              )
                            }
                            src={singleCross}
                            alt="cross"
                            className="cross-img pointer"
                          ></img>
                        </div>
                      );
                    })}
                  </div>
                </span>
              </div>
              {(coachesList.length > 0 || chosenCoach !== "") && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">
                    {chosenCoachIsHittingPartner ? "Hitting Partner" : "Tränare"}
                  </span>
                  <span className="text-m primary-text text-svg-wrapper">
                    {chosenCoachData && (
                      <div className="added-coach-remove-wrapper">
                        <span>
                          {chosenCoachData.first_name}{" "}
                          {chosenCoachData.last_name} (
                          {chosenCoachData.coach_id !== "5513"
                            ? chosenCoachData.coach_price +
                              50 * chosenCoPlayers.length
                            : chosenCoachData.coach_price}{" "}
                          kr)
                        </span>
                        <img
                          onClick={removeCoach(chosenCoach)}
                          src={singleCross}
                          alt="cross"
                          className="cross-img pointer"
                        ></img>
                      </div>
                    )}
                  </span>
                </div>
              )}
              {props.isAdmin && (
                <>
                  <div className="general-popup-flex-between">
                    <span className="text-m secondary-text">
                      Skicka Email vid ändring
                    </span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        name="closed"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(!sendEmail)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <div className="general-popup-flex-between">
                    <span className="text-m secondary-text">Drop in</span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        name="closed"
                        checked={isDropIn}
                        onChange={(e) => setIsDropIn(!isDropIn)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="general-popup-lila-border-group">
            <div className="co-player-trainer-wrapper">
              <div className="user-booking-court-type">
                <div className="user-booking-text-toggle-wrapper">
                  <div className="user-bookings-toggle-text text-l">
                    {currentAdditiveText}
                  </div>
                  <div className="users-bookings-toggle-buttons">
                    {additiveOptions.map((option, index) => {
                      const trainersAvailale = selectableCoachIds.length > 0;
                      const bookingHasVilandeSpelratt = chosenCoPlayers.some(
                        (coPlayer) =>
                          coPlayer.member_type === "Vilande spelrätt" ||
                          coPlayer.member_type === "Vilande spelrätt..",
                      );

                      if (option.value === "HITTING_PARTNER") {
                        if (!canShowHittingPartnerTab) return null;
                      } else {
                        if (
                          !trainersAvailale ||
                          guests.length > 0 ||
                          bookingHasVilandeSpelratt
                        )
                          return null;
                        if (chosenCoachIsHittingPartner) return null;
                      }

                      return (
                        <div
                          key={index}
                          className="user-booking-additive-toggle-wrapper"
                        >
                          <button
                            className={`toggle-button text-m ${
                              currentAdditiveChoice.value === option.value
                                ? "active"
                                : ""
                            }`}
                            onClick={() =>
                              handleAdditiveChoiceToggle(
                                option as IAdditiveOption,
                              )
                            }
                          >
                            {option.label}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(currentAdditiveChoice.value === "TRAINER" ||
                  currentAdditiveChoice.value === "HITTING_PARTNER") &&
                  selectableCoachIds.length > 0 && (
                    <div className="coach-choices-wrapper text-m">
                      <div>
                        <span>
                          {currentAdditiveChoice.value === "HITTING_PARTNER"
                            ? "Tillgängliga Hitting Partners"
                            : "Tillgängliga tränare"}
                        </span>
                      </div>
                      {selectableCoachIds.map((coach, index) => {
                        const coachWidthData = coaches.find(
                          (c) => c.coach_id === coach,
                        );
                        if (!coachWidthData) return;
                        const isHittingPartner =
                          !!coachWidthData.is_hitting_partner;
                        if (
                          currentAdditiveChoice.value === "HITTING_PARTNER" &&
                          !isHittingPartner
                        )
                          return null;
                        if (
                          currentAdditiveChoice.value === "TRAINER" &&
                          isHittingPartner
                        )
                          return null;
                        return (
                          <div
                            key={index}
                            className="coach-wrapper pointer"
                            onClick={() => addCoach(coach)}
                          >
                            <div className="coach-name">
                              {capitalizeWords(coachWidthData.first_name)}{" "}
                              {capitalizeWords(coachWidthData.last_name)}
                            </div>
                            <div className="coach-price-id-wrapper">
                              <div className="coach-id">({coach})</div>
                              <div className="coach-price">
                                {coachWidthData.coach_price} kr
                              </div>
                            </div>
                            <a className="choose-coach">
                              {isHittingPartner
                                ? " Välj Hitting Partner"
                                : " Välj Coach"}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {currentAdditiveChoice.value === "CO-PLAYER" && (
                  <div className="co-player-choices-wrapper text-m">
                    <div>
                      <span>Senaste medspelare</span>
                    </div>
                    {availableCoPlayers.length > 0 ? (
                      availableCoPlayers.map((player, index) => {
                        if (
                          chosenCoPlayers.some(
                            (coPlayer) =>
                              coPlayer.member_id === player.member_id,
                          ) ||
                          player.member_id === props.user?.member_id
                        ) {
                          return null;
                        }
                        if (player.member_type === "Före detta medlem")
                          return null;
                        if (index > 4) return null;
                        const isFrozen = player.frozen;
                        return (
                          <div
                            key={index}
                            className="player-wrapper pointer"
                            onClick={() =>
                              addPlayerAsCoPlayer(player, "available")
                            }
                          >
                            <div
                              className={`player-name ${
                                isFrozen ? "frozen" : ""
                              }`}
                            >
                              {capitalizeWords(player.first_name)}{" "}
                              {capitalizeWords(player.last_name)}
                            </div>
                            <div className="player-id">
                              {" "}
                              {player.member_id}{" "}
                            </div>
                            <a className="choose-player"> Välj medspelare</a>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-latest-players secondary-text">
                        Du har inte spelat med någon tidigare
                      </div>
                    )}
                    <div>
                      <span>Övriga medspelare</span>
                    </div>
                    <div className="search-input-btn-wrapper">
                      <div className="search-input-wrapper">
                        <input
                          className="users-search-input text-m"
                          value={searchString}
                          onChange={(e) => setSearchString(e.target.value)}
                          placeholder="Sök på namn & medlemsnummer"
                        ></input>
                        <i className="fa-regular fa-magnifying-glass search-icon"></i>
                      </div>
                    </div>
                    {searchedUsers.length > 0 &&
                      searchedUsers
                        .filter(
                          (user) =>
                            !availableCoPlayers.some(
                              (coPlayer) =>
                                coPlayer.member_id === user.member_id,
                            ) &&
                            !chosenCoPlayers.some(
                              (coPlayer) =>
                                coPlayer.member_id === user.member_id,
                            ) &&
                            user.member_id !== props.user?.member_id,
                        )
                        .map((player, index) => {
                          // if (player.member_type === "Före detta medlem")
                          //   return null;
                          if (index > 4) return null;
                          const isFrozen = player.frozen;
                          return (
                            <div key={index} className="player-wrapper pointer">
                              <div
                                className={`player-name ${
                                  isFrozen ? "frozen" : ""
                                }`}
                              >
                                <span>
                                  {capitalizeWords(player.first_name)}{" "}
                                  {capitalizeWords(player.last_name)}
                                </span>
                              </div>
                              <div className="player-id">
                                {player.member_id}
                              </div>
                              <a
                                className="choose-player"
                                onClick={() =>
                                  addPlayerAsCoPlayer(player, "filtered")
                                }
                              >
                                Välj medspelare
                              </a>
                            </div>
                          );
                        })}

                    <div className="add-guest-wrapper flex-row">
                      <button
                        onClick={() => addGuest()}
                        disabled={totalCoPlayers === 3 || chosenCoach !== ""}
                      >
                        Lägg till gäst
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </Popup>
  );
}

export default EditCoPlayersPopup;
